import { differenceInCalendarMonths, isAfter } from 'date-fns';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import Button from '../../atoms/Button';
import AmountField from '../../molecules/AmountField/AmountField';
import InterestField from '../../molecules/InterestField/InterestField';
import TextField from '../../molecules/TextField';
import Tooltip from '../../molecules/Tooltip';
import deepDefaultProps from '../../util/deepDefaultProps';
import {
  getBestRateForAmount,
  getProduct,
  purposeMap,
  turnIntoDateObject,
} from '../../util/formHelper';
import { formatAmount } from '../../util/numberFormatter';
import ErrorMessage from './Error';
import LegalText from './LegalText';
import {
  ButtonWrapper,
  Comparison,
  Fieldset,
  FlexingContainer,
  Form,
  HorizontalLine,
  InputContainer,
  MaxWidthRow,
  NewCreditAmount,
  NewCreditAttribute,
  SavingAmount,
  Section,
  Title,
} from './RefinancingForm.style';
import WidgetFooter from './WidgetFooter';

const PRODUCT_ID = process.env.REACT_APP_OFINA_PRODUCT_ID;

const calculateCreditValues = (rate, runtime, amount) => {
  const costs = (rate / 100 / (12 / (runtime + 1))) * amount;
  const total = amount + costs;
  const monthlyPayment = total / runtime;
  return [costs, total, monthlyPayment];
};

class RefinancingForm extends PureComponent {

  static propTypes = {
    affiliateData: PropTypes.object.isRequired,
    submitUrl: PropTypes.instanceOf(URL).isRequired,
    resolveToUrl: PropTypes.func.isRequired,
    resolveWithLoader: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.formElement = React.createRef();

    this.state = {
      hasError: false,
      oldCredit: null,
      newCredit: null,
      defaultCredit: null,
    };
    this.loadProduct()
      .then(this.updateOffer)
      .catch(({ message }) => this.setError(message));

    this.debouncedUpdateOffer = debounce(
      this.updateOffer,
      500,
    );
  }

  componentDidUpdate = (_, prevState) => {
    const { creditAmount, effectiveInterest, runtime, startDate } = prevState;
    if (
      this.state.creditAmount !== creditAmount ||
      this.state.effectiveInterest !== effectiveInterest ||
      this.state.runtime !== runtime ||
      this.state.startDate !== startDate
    ) {
      this.updateOffer().catch(({ message }) => {
        this.setError(message);
      });
    }
  };

  setError = error => this.setState({ hasError: true, error });

  loadProduct = () =>
    new Promise((resolve, reject) => {
      const {
        affiliateData: { advertisementId },
      } = this.props;

      getProduct(PRODUCT_ID, advertisementId)
        .then((product) => {
          if (
            product &&
            product.conditions &&
            Array.isArray(product.conditions)
          ) {
            const defaultCredit = product.conditions[0];
            defaultCredit.monthlyPayment =
              ((defaultCredit.effectiveInterestRate /
                100 /
                (12 / (defaultCredit.termMin + 1))) *
                defaultCredit.amountMin +
                defaultCredit.amountMin) /
              defaultCredit.termMin;

            this.setState({ product, defaultCredit }, resolve);
          }
        })
        .catch(reject);
    });

  updateOffer = async () => {
    const { creditAmount, effectiveInterest, runtime, startDate } = this.state;
    if (!runtime || !creditAmount || !effectiveInterest || !startDate) {
      this.setState({
        newCredit: null,
        saving: null,
        hasError: false,
        error: null,
      });
      return;
    }

    const costs =
      (effectiveInterest / 100 / (12 / (runtime + 1))) * creditAmount;
    const total = creditAmount + costs;

    const startDateObj = turnIntoDateObject(startDate);
    const today = new Date();

    // check if starts in the future
    if (isAfter(startDateObj, today)) {
      throw new Error(
        'Sie können keinen Kredit umschulden der noch nicht begonnen hat.',
      );
    }

    const monthsTillStart = differenceInCalendarMonths(today, startDateObj);
    const oldCredit = {
      runtime,
      remainingRuntime: runtime - monthsTillStart,
      amount: creditAmount,
      effectiveInterest,
      costs,
      total,
      monthlyPayment: total / runtime,
    };

    let newRuntime = runtime - monthsTillStart;
    if (newRuntime <= 0) {
      throw new Error('Sie haben ihren Kredit bereits abbezahlt.');
    }
    // round up runtime to multiple of 12 to work with fidor/cafe api
    if (newRuntime % 12 !== 0) {
      newRuntime = (Math.floor(newRuntime / 12) + 1) * 12;
    }

    const payed = (monthsTillStart / oldCredit.runtime) * oldCredit.amount;
    const newAmount = oldCredit.amount - payed;

    const { product } = this.state;
    if (!product) {
      throw new Error('Leider können wir Ihnen derzeit kein Angebot machen.');
    }

    const {
      affiliateData: { advertisementId },
    } = this.props;
    const bestOffer = await getBestRateForAmount(newAmount, advertisementId);

    if (!bestOffer) {
      throw new Error(
        'Leider können wir Ihnen unter diesen Bedingungen kein Angebot machen.',
      );
    }

    // check possible terms
    if (newRuntime < bestOffer.termMin) {
      newRuntime = bestOffer.termMin;
    }
    else if (newRuntime > bestOffer.termMax) {
      newRuntime = bestOffer.termMax;
    }

    const {
      effectiveInterestRate: newEffectiveInterestRate,
      nominalInterestRate: newNominalInterestRate,
      effectiveInterestRateTwoThird,
      nominalInterestRateTwoThird,
      bank: { name: bank },
    } = bestOffer;
    if (newEffectiveInterestRate >= effectiveInterest) {
      throw new Error(
        'Leider können wir Ihnen kein günstigeres Angebot machen.',
      );
    }

    const [effCosts, effTotal, effMonthly] = calculateCreditValues(
      newEffectiveInterestRate,
      newRuntime,
      newAmount,
    );
    const [
      twoThirdCosts,
      twoThirdTotal,
      twoThridMonthly,
    ] = calculateCreditValues(
      effectiveInterestRateTwoThird,
      newRuntime,
      newAmount,
    );

    const newCredit = {
      monthsTillStart,
      runtime: newRuntime,
      term: newRuntime,
      payed,
      amount: newAmount,
      costs: effCosts,
      total: effTotal,
      monthlyPayment: effMonthly,
      effectiveInterestRate: newEffectiveInterestRate,
      nominalInterestRate: newNominalInterestRate,
      effectiveInterestRateTwoThird,
      nominalInterestRateTwoThird,
      twoThirdCosts,
      twoThirdTotal,
      twoThridMonthly,
      bank,
    };

    const saving =
      oldCredit.monthlyPayment * oldCredit.remainingRuntime -
      newCredit.monthlyPayment * newCredit.runtime;

    this.setState({
      newCredit,
      oldCredit,
      saving,
      hasError: false,
      error: null,
    });
  };

  handleChange = stateKey => (event, value) => {
    this.setState({
      [stateKey]: value,
    });
  };

  handleChangeStartDate = (event) => {
    this.setState({
      startDate: event.target.value,
    });
  };

  handleSubmit = (event) => {
    event.preventDefault();
    // make post request to submit url
    const { newCredit } = this.state;
    const requestData = {
      ...this.props.affiliateData,
      productId: this.props.affiliateData
        ? Number(this.props.affiliateData.productId)
        : null,
      amount: Math.ceil(newCredit.amount),
      term: newCredit.runtime,
      purpose: purposeMap.REFINANCING.toUpperCase(),
    };

    const request = new Request(this.formElement.current.action, {
      method: 'POST',
      headers: new Headers({
        Accept: 'application/json',
      }),
      body: JSON.stringify(requestData),
    });

    const promise = fetch(request)
      .then((response) => {
        if (!response.ok) {
          this.setState({
            hasError: true,
            error:
              'Der Server meldete einen Fehler. Bitte überprüfen Sie Ihre Daten noch einmal!',
          });
          throw new Error(
            `Server responded with status ${response.status} - ${response.statusText}`,
          );
        }

        if (
          !response.headers.get('Content-Type').match(/^application\/json\b/)
        ) {
          throw new Error(
            'Bad content type: ' + response.headers.get('Content-Type'),
          );
        }

        return response.json().then((data) => {
          sessionStorage.setItem(
            process.env.REACT_APP_STORAGE_KEY_RESUME_HASH,
            data.result.resumeHash,
          );
          data.request = requestData; // extend data object for callback
          this.setState({ hasError: false });
          return data;
        });
      })
      .catch((reason) => {
        if (!this.state.hasError) {
          this.setState({
            hasError: true,
            error:
              'Beim Übertragen der Daten ist ein Fehler aufgetreten. Bitte versuchen Sie es später noch einmal!',
          });
        }
        console.error('API request failed: ' + reason);
      });

    // resolve promise with loading indicator and open the resulting cafe url
    const { resolveToUrl, resolveWithLoader } = this.props;
    resolveToUrl(resolveWithLoader(promise));
  };

  renderNewCredit = (amount, monthlyPayment, interestRate, runtime) => (
    <MaxWidthRow>
      <InputContainer>
        <Fieldset>
          <Section>
            Ihr Umschuldungskredit
            <Tooltip
              size={18}
              style={{ margin: '0', alignSelf: 'flex-start' }}
              text="Hier sehen Sie die möglichen Konditionen Ihres neuen Kredits."
            />
          </Section>

          <NewCreditAttribute
            label="Kreditbetrag"
            value={formatAmount(amount, '€', 2)}
          />

          <NewCreditAttribute
            label="Monatliche Rate"
            value={formatAmount(monthlyPayment, '€', 2)}
          />

          <NewCreditAttribute
            label="Effektiver Jahreszins ab"
            value={`${interestRate}%*`}
          />

          <NewCreditAttribute label="Laufzeit" value={`${runtime} Monate`} />
        </Fieldset>
      </InputContainer>
    </MaxWidthRow>
  );

  render() {
    const {
      effectiveInterest,
      creditAmount,
      runtime,
      startDate,
      hasError,
      defaultCredit,
      newCredit,
      oldCredit,
      saving,
      error,
    } = this.state;

    return (
      <div>
        <Title>Umschuldungsrechner</Title>

        {hasError && <ErrorMessage message={error} />}

        <Form
          ref={this.formElement}
          action={newCredit ? this.props.submitUrl : null}
          method="post"
          onChange={this.handleValidity}
          onInvalid={this.handleInvalid}
          onSubmit={this.handleSubmit}
        >
          <FlexingContainer>
            <MaxWidthRow>
              <InputContainer>
                <Fieldset>
                  <Section>
                    Ihr aktueller Kredit
                    <Tooltip
                      size={18}
                      style={{ margin: '0', alignSelf: 'flex-start' }}
                      text="Tragen Sie hier die Daten Ihres aktuell laufenden Kredits ein, den Sie umschulden möchten.
                    Welche Kreditsumme haben Sie damals aufgenommen? Wann wurde der Kredit bestätigt?"
                    />
                  </Section>

                  <AmountField
                    name="creditAmount"
                    label="Ursprünglicher Kreditbetrag"
                    onChange={this.handleChange('creditAmount')}
                    placeholder="15000,00"
                    value={creditAmount}
                    unit="€"
                    required
                    autoComplete="off"
                  />

                  <TextField
                    name="startDate"
                    label="Abschlussdatum"
                    onChange={this.handleChangeStartDate}
                    onInput={this.handleChangeStartDate}
                    placeholder={`01.${new Date().getFullYear()}`}
                    pattern="^\s*((3[01]|[12][0-9]|0?[1-9])\.)?(1[012]|0?[1-9])\.((?:19|20)\d{2})\s*$"
                    value={startDate}
                    min={1}
                    max={120}
                    required
                    autoComplete="off"
                  />

                  <InterestField
                    name="effectiveInterest"
                    label="Effektiver Jahreszins"
                    onChange={this.handleChange('effectiveInterest')}
                    placeholder="4,00"
                    value={effectiveInterest}
                    min={0}
                    max={100}
                    unit="% p.a."
                    required
                    autoComplete="off"
                  />

                  <AmountField
                    name="runtime"
                    label="Laufzeit"
                    onChange={this.handleChange('runtime')}
                    placeholder="48"
                    value={runtime}
                    min={1}
                    max={240}
                    unit="Monate"
                    required
                    autoComplete="off"
                    maxLength={3}
                  />

                  {!hasError && newCredit && (
                    <NewCreditAmount
                      value={formatAmount(newCredit.amount, '€', 2)}
                    />
                  )}
                </Fieldset>
              </InputContainer>
            </MaxWidthRow>

            {/* New Credit */}
            {newCredit
              ? this.renderNewCredit(
                newCredit.amount,
                newCredit.monthlyPayment,
                newCredit.effectiveInterestRate,
                newCredit.runtime,
              )
              : defaultCredit &&
              this.renderNewCredit(
                defaultCredit.amountMin,
                defaultCredit.monthlyPayment,
                defaultCredit.effectiveInterestRate,
                defaultCredit.termMin,
              )}
          </FlexingContainer>

          {/* Comparison */}
          {!hasError && oldCredit && newCredit && (
            <div>
              <Section>Ihre mögliche Einsparung</Section>

              <FlexingContainer>
                <MaxWidthRow>
                  <Comparison
                    title="Monatliche Rate"
                    tooltipText="Hier finden Sie den Vergleich der aktuellen und der neuen monatlichen Rate."
                    oldValue={formatAmount(oldCredit.monthlyPayment, '€', 2)}
                    newValue={formatAmount(newCredit.monthlyPayment, '€', 2)}
                  />
                </MaxWidthRow>
                <MaxWidthRow>
                  <Comparison
                    title="Kreditkosten"
                    // eslint-disable-next-line max-len
                    tooltipText="Hier sehen Sie einen Vergleich der Kreditkosten für den abzulösenden und den neuen Kre-dit über die Restlaufzeit."
                    oldValue={formatAmount(
                      (oldCredit.runtime - newCredit.monthsTillStart) *
                      oldCredit.monthlyPayment -
                      newCredit.amount,
                      '€',
                      2,
                    )}
                    newValue={formatAmount(newCredit.costs, '€', 2)}
                    oldLabel="aktueller Kredit (Rest)"
                  />
                </MaxWidthRow>
              </FlexingContainer>
              <HorizontalLine />
            </div>
          )}

          {/* Saving & Button & LegalText */}
          {!hasError && (
            <div>
              {saving && (
                <SavingAmount
                  value={formatAmount(saving, '€', 2)}
                  tooltipText="Hier sehen Sie, wie viel Geld Sie mit dem neuen Kredit sparen können."
                />
              )}
              <FlexingContainer wrapReverse>
                {!hasError && oldCredit && newCredit && (
                  <LegalText
                    amount={newCredit.amount}
                    nominalInterestRate={newCredit.nominalInterestRateTwoThird}
                    monthlyPayment={newCredit.twoThridMonthly}
                    effectiveInterestRate={
                      newCredit.effectiveInterestRateTwoThird
                    }
                    total={newCredit.twoThirdTotal}
                    bank={newCredit.bank}
                  />
                )}

                <ButtonWrapper
                  withMargin={!(!hasError && oldCredit && newCredit)}
                >
                  <Button
                    type="submit"
                    style={{ margin: '0 0 1em 0' }}
                    label={
                      !newCredit
                        ? 'Neuen Kredit berechnen'
                        : 'Jetzt Vergleichen'
                    }
                  />
                </ButtonWrapper>
              </FlexingContainer>
            </div>
          )}

          <HorizontalLine />

          <WidgetFooter />
        </Form>
      </div>
    );
  }

}

export default deepDefaultProps({})(RefinancingForm);
